/**
 * Audit Logs API Service (PR A9)
 * API calls for audit log viewing and export
 */
import { apiRequest } from './adminApi';

/**
 * List audit logs with filters
 */
export const listAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Date filters
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  
  // Filter by actor, impersonator, resource, action
  if (filters.actor_id) params.append('actor_id', filters.actor_id);
  if (filters.impersonator_id) params.append('impersonator_id', filters.impersonator_id);
  if (filters.resource) params.append('resource', filters.resource);
  if (filters.action) params.append('action', filters.action);
  
  // Free text search
  if (filters.query) params.append('query', filters.query);
  
  // Pagination
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.page_size) params.append('page_size', filters.page_size.toString());
  
  // Sort
  if (filters.sort) params.append('sort', filters.sort);
  
  return await apiRequest(`/admin/audit-logs?${params.toString()}`);
};

/**
 * Get single audit log by ID
 */
export const getAuditLog = async (logId) => {
  return await apiRequest(`/admin/audit-logs/${logId}`);
};

/**
 * Export audit logs as CSV
 */
export const exportAuditLogsCSV = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Same filters as list
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.actor_id) params.append('actor_id', filters.actor_id);
  if (filters.impersonator_id) params.append('impersonator_id', filters.impersonator_id);
  if (filters.resource) params.append('resource', filters.resource);
  if (filters.action) params.append('action', filters.action);
  if (filters.query) params.append('query', filters.query);
  
  const url = `/admin/audit-logs/export?${params.toString()}`;
  
  // Download CSV
  const token = localStorage.getItem('token');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const apiBase = backendUrl ? `${backendUrl}/api` : '/api';
  const response = await fetch(`${apiBase}${url}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (response.status === 413) {
    throw new Error('Export too large. Please narrow your filters.');
  }
  
  if (response.status === 429) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Export failed' }));
    throw new Error(error.detail || 'Export failed');
  }
  
  // Create download link
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Get unique resources from audit logs (for dropdown)
 */
export const getAuditResources = async () => {
  // This would ideally be a dedicated endpoint
  // For now, return common resources
  return [
    'users', 'roles', 'rbac', 'orders', 'products',
    'templates', 'settings', 'flags', 'audit'
  ];
};

/**
 * Get unique actions from audit logs (for dropdown)
 */
export const getAuditActions = async () => {
  // Common audit actions
  return [
    'view', 'create', 'update', 'delete', 'export',
    'approve', 'impersonate', 'lock', 'unlock',
    'reset_password', 'start_impersonation', 'stop_impersonation',
    'update_rbac_matrix', 'update_user_roles'
  ];
};
